import { ViewStyle } from "react-native";
import { StyledButton } from "./styled";

const sizeStyles = {
  large: { label: "py-4 px-8 m-0", roundness: 3.3, minHeight: 48 },
  small: { label: "py-2 px-4 m-0", roundness: 2.5, minHeight: 32 },
  block: { label: "py-4 px-8 m-0", roundness: 3.2, minHeight: 48 },
  filter: { label: "my-1 px-2", roundness: 1, minHeight: 10 },
};

type Mode = "outlined" | "text" | "contained" | "secondary" | "light" | "selected" | "rejected";

type Size = keyof typeof sizeStyles;

type Position = "left" | "right";
type Alignment = "adjacent" | "edge";

export interface Props extends Omit<React.ComponentProps<typeof StyledButton>, "mode"> {
  size?: Size;
  mode?: Mode;
  isValid?: boolean;
  style?: ViewStyle;
  borderColor?: string;
  iconPosition?: Position;
  iconAlignment?: Alignment;
}

export default function Button({
  size = "large",
  iconPosition = "left",
  iconAlignment = "edge",
  disabled,
  mode,
  className,
  labelStyle,
  isValid = true,
  style,
  borderColor,
  ...props
}: Props) {
  const sizeStyle = sizeStyles[size];
  const modeStyle = getModeStyle(mode, disabled, isValid, borderColor);

  return (
    <StyledButton
      style={[{ borderColor: borderColor || "border-primary", minHeight: sizeStyle.minHeight }, style]}
      {...props}
      disabled={disabled}
      mode={modeStyle.mode}
      theme={{ roundness: sizeStyle.roundness }}
      contentStyle={[
        props.icon && iconPosition === "left"
          ? {
              flexDirection: "row",
            }
          : null,
        props.icon && iconPosition === "right"
          ? {
              flexDirection: "row-reverse",
            }
          : null,
        { alignItems: "center" },
        { gap: iconAlignment === "edge" ? -16 : 8 },
      ]}
      icon={props.icon}
      className={`
        ${modeStyle.button}
        ${props.icon && iconAlignment === "edge" ? "justify-between" : "justify-center"}
        ${className}
      `}
      labelStyle={`
        text-regular-tight
        font-500
        ${modeStyle.label}
        ${sizeStyle.label}
        leading-4
        ${labelStyle}
        ${props.icon && iconAlignment === "edge" ? "flex-1 text-center" : ""}
      `}
      children={props.children}
    />
  );
}

type ModeStyle = {
  mode: "contained" | "text" | "outlined";
  label: string;
  button: string;
};

export function getModeStyle(
  mode: Mode = "text",
  disabled: boolean = false,
  isValid: boolean = false,
  borderColor?: string
): ModeStyle {
  let style;
  const key = [mode, isValid ? "valid" : "invalid", disabled ? "disabled" : "enabled"].join(",");

  switch (key) {
    case "contained,invalid,disabled":
    case "contained,invalid,enabled":
    case "contained,valid,disabled":
      style = { button: "bg-sky-light", label: "text-sky-dark" };
      break;
    case "contained,valid,enabled":
      style = { button: "bg-primary", label: "text-sky-white" };
      break;
    case "selected,valid,enabled":
      style = { button: "bg-green-light", label: "text-sky-white" };
      break;
    case "rejected,valid,enabled":
      style = { button: "bg-red", label: "text-sky-white" };
      break;
    case "outlined,invalid,enabled":
    case "outlined,valid,enabled":
      style = { button: borderColor || "border-primary" };
      break;
    case "outlined,invalid,disabled":
    case "outlined,valid,disabled":
      style = { button: "border-sky" };
      break;
    case "secondary,valid,enabled":
      style = { button: "bg-primary-lightest active:opacity-80", label: "text-primary" };
      break;
    case "light,valid,enabled":
      style = { button: "bg-sky-lighter active:opacity-80", label: "text-primary" };
      break;
  }
  const containedModes = ["secondary", "light", "rejected", "selected"];
  const finalMode = containedModes.includes(mode) ? "contained" : mode === "outlined" || mode === "text" ? mode : "contained";
  return { mode: finalMode, button: "", label: "", ...style };
}
